import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Box, Alert, Text, Stack, Center } from "@mantine/core";
import { LoadingIndicator } from "../../generic/LoadingIndicator";
import { useMedia } from "@/hooks";
import {
  detectVideoType,
  getSupportedPlatforms,
  needsProgrammaticPlay,
} from "./videoUtils";

export interface VideoContentBlockProps {
  /** Video URL (supports multiple platforms - see videoUtils.ts) */
  videoUrl: string | null | undefined;

  /** Additional CSS class name for styling */
  className?: string;

  /** Whether to autoplay the video */
  autoplay?: boolean;

  /** Whether to mute the video */
  muted?: boolean;

  /** Whether to loop the video */
  loop?: boolean;

  /** Whether to show video controls */
  controls?: boolean;

  themeConfig?: any;

  /** Callback fired when video loads successfully */
  onLoad?: (() => void) | undefined;

  /** Callback fired when video fails to load */
  onError?: ((error: string) => void) | undefined;

  /** Callback fired when video starts playing */
  onPlay?: (() => void) | undefined;

  /** Callback fired when video is paused */
  onPause?: (() => void) | undefined;

  /** Callback fired when video is ended */
  onEnded?: (() => void) | undefined;
}

export const VideoContentBlock: React.FC<VideoContentBlockProps> = ({
  videoUrl,
  className,
  autoplay = true,
  muted = true, // Default to muted for autoplay to work
  loop = true,
  controls = false, // Hide controls for TV display
  onLoad,
  onError,
  onPlay,
  onPause,
  onEnded,
}) => {
  // Detect video type and generate embed URL
  const videoInfo = useMemo(() => {
    return detectVideoType(videoUrl, { autoplay, muted, loop, controls });
  }, [videoUrl, autoplay, muted, loop, controls]);

  // Direct video files (mp4/webm/…) are uploaded media, so route them through
  // the session media cache: fetched once, then served from an in-memory blob
  // URL on every re-appearance — no reload, no network dependency. Third-party
  // embeds (YouTube, Vimeo, …) are cross-origin streams and can't be cached.
  const directVideoUrl =
    videoInfo.type === "direct" ? videoInfo.embedUrl : null;
  const {
    src: cachedVideoSrc,
    isCached: videoIsCached,
    originalUrl: originalVideoUrl,
    reportError: reportVideoCacheError,
  } = useMedia(directVideoUrl);

  // Skip the loading spinner when the direct video is already cached.
  const [loading, setLoading] = useState(!videoIsCached);
  const [error, setError] = useState<string | null>(null);
  // If a cached blob fails to play, we fall back to streaming from the network.
  const [useNetworkFallback, setUseNetworkFallback] = useState(false);

  const effectiveVideoSrc = useNetworkFallback
    ? (originalVideoUrl ?? videoInfo.embedUrl)
    : (cachedVideoSrc ?? videoInfo.embedUrl);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playAttemptIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Attempt to play videos programmatically for platforms that need it
  const attemptProgrammaticPlay = useCallback(() => {
    if (!iframeRef.current || !needsProgrammaticPlay(videoInfo.type)) return;

    try {
      const iframe = iframeRef.current;

      // Try different postMessage approaches
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: "play" }),
        "*",
      );
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func: "playVideo" }),
        "*",
      );
      iframe.contentWindow?.postMessage("play", "*");

      // Simulate click event
      const clickEvent = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window,
      });
      iframe.dispatchEvent(clickEvent);
    } catch (err) {
      console.warn(`${videoInfo.type} autoplay attempt failed:`, err);
    }
  }, [videoInfo.type]);

  // Handle iframe load and attempt autoplay if needed
  const handleIframeLoad = useCallback(() => {
    handleLoad();

    // Attempt programmatic play for platforms that need it
    if (autoplay && needsProgrammaticPlay(videoInfo.type)) {
      // Clear any existing interval
      if (playAttemptIntervalRef.current) {
        clearInterval(playAttemptIntervalRef.current);
      }

      // Try immediately after a short delay
      setTimeout(() => {
        attemptProgrammaticPlay();
      }, 500);

      // Then retry every 2 seconds for up to 10 seconds
      let attempts = 0;
      playAttemptIntervalRef.current = setInterval(() => {
        attempts++;
        attemptProgrammaticPlay();

        // Stop after 5 attempts (10 seconds)
        if (attempts >= 5 && playAttemptIntervalRef.current) {
          clearInterval(playAttemptIntervalRef.current);
          playAttemptIntervalRef.current = null;
        }
      }, 2000);
    }
  }, [autoplay, videoInfo.type, attemptProgrammaticPlay]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (playAttemptIntervalRef.current) {
        clearInterval(playAttemptIntervalRef.current);
      }
    };
  }, []);

  const handleLoad = useCallback(() => {
    setLoading(false);
    setError(null);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(
    (errorMessage: string) => {
      setLoading(false);
      setError(errorMessage);
      onError?.(errorMessage);
    },
    [onError],
  );

  const handleVideoLoad = useCallback(() => {
    handleLoad();
  }, [handleLoad]);

  const handleVideoError = useCallback(() => {
    // A cached blob failed to play (e.g. a picky TV video decoder). Drop it and
    // retry by streaming from the network before showing an error.
    if (
      !useNetworkFallback &&
      typeof effectiveVideoSrc === "string" &&
      effectiveVideoSrc.startsWith("blob:") &&
      originalVideoUrl
    ) {
      reportVideoCacheError();
      setUseNetworkFallback(true);
      setLoading(true);
      setError(null);
      return;
    }
    handleError("Failed to load video file");
  }, [
    handleError,
    useNetworkFallback,
    effectiveVideoSrc,
    originalVideoUrl,
    reportVideoCacheError,
  ]);

  const handleVideoPlay = useCallback(() => {
    onPlay?.();
  }, [onPlay]);

  const handleVideoPause = useCallback(() => {
    onPause?.();
  }, [onPause]);

  const handleVideoEnded = useCallback(() => {
    onEnded?.();
  }, [onPause]);

  // Show error state
  if (error || videoInfo.type === "unknown") {
    const errorMessage = error || "Unsupported video format or invalid URL";

    return (
      <Box
        className={`video-content-block video-content-block--error ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="red"
            title="Video Error"
            className="video-content-block__error-alert"
          >
            <Text
              size="sm"
              className="video-content-block__error-message"
              ta="center"
            >
              {errorMessage}
            </Text>
            <Text
              size="xs"
              c="dimmed"
              mt="xs"
              className="video-content-block__error-subtitle"
              ta="center"
            >
              Supported formats: {getSupportedPlatforms()}
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      className={`video-content-block video-content-block--${videoInfo.type} ${
        className || ""
      }`}
      h="100%"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Loading indicator */}
      {loading && (
        <Center
          className="video-content-block__loading"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.1)",
          }}
        >
          <Stack align="center" gap="md">
            <LoadingIndicator
              size="lg"
              className="video-content-block__loading-indicator"
            />
            <Text
              size="sm"
              c="dimmed"
              className="video-content-block__loading-text"
              ta="center"
            >
              Loading video...
            </Text>
          </Stack>
        </Center>
      )}

      {/* Embedded video (iframe-based platforms) */}
      {videoInfo.type !== "direct" && videoInfo.embedUrl && (
        <iframe
          ref={iframeRef}
          src={videoInfo.embedUrl}
          className="video-content-block__iframe"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            opacity: loading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          onLoad={handleIframeLoad}
          title="Video content"
        />
      )}

      {/* Direct video file */}
      {videoInfo.type === "direct" && (
        <video
          // Remount when switching between the cached blob and the network URL
          // so the video element reliably reloads on all (incl. TV) browsers.
          key={useNetworkFallback ? "network" : "cache"}
          src={effectiveVideoSrc ?? videoInfo.embedUrl!}
          className="video-content-block__video"
          autoPlay={autoplay}
          muted={muted}
          loop={loop}
          controls={controls}
          playsInline
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          onEnded={handleVideoEnded}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            opacity: loading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      )}
    </Box>
  );
};
